import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Bookmark } from '../../../models/bookmark';
import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { ContextMenuModule } from 'primeng/contextmenu';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { MenuItem, MenuItemCommandEvent, MessageService } from 'primeng/api';
import { BaseListComponent } from '../base-list/base-list.component';
import { SidebarService } from '../../../services/menu/sidebar.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { SidebarModule } from 'primeng/sidebar';
import { RoutingService } from '../../../services/routing.service';
import { GlobalsStore } from '../../../stores/globals.state';
import { PermissionService } from '../../../services/permission.service';
import { checkVisibleMenuItems } from '../../../utility/array.utility';
import { Button } from 'primeng/button';
import { CommuneStore } from '../../../stores/commune/commune.state';
import { GuestLinkService } from '../../../services/bookmark/guest-link.service';

@Component({
  selector: 'app-bookmark-list',
  templateUrl: './bookmark-list.component.html',
  styleUrls: ['./bookmark-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgForOf,
    NgClass,
    NgIf,
    ContextMenuModule,
    CdkDropList,
    CdkDrag,
    SidebarModule,
    Button,
  ],
})
export class BookmarkListComponent extends BaseListComponent<Bookmark> {
  readonly store = inject(BookmarkStore);
  readonly globalsStore = inject(GlobalsStore);
  readonly activeUserStore = inject(ActiveUserStore);
  readonly communeStore = inject(CommuneStore);
  override sidebarId = 'bookmark-administration';
  contextMenuBookmarks: MenuItem[] = [];

  @Input() bookmarks: Bookmark[] = [];
  @Input() listId = '';
  @Input() connectedTo: string[] = [];
  @Output() itemDropped: EventEmitter<CdkDragDrop<Bookmark[]>> =
    new EventEmitter();
  @Output() contextMenuRequest: EventEmitter<{
    event: Event;
    bookmark: Bookmark;
  }> = new EventEmitter();

  constructor(
    protected sidebarService: SidebarService,
    router: Router,
    route: ActivatedRoute,
    messageService: MessageService,
    confirmationDialogService: ConfirmationDialogService,
    routingService: RoutingService,
    private guestLinkService: GuestLinkService,
    private permissionService: PermissionService
  ) {
    super(
      router,
      route,
      messageService,
      confirmationDialogService,
      routingService
    );
  }

  /**
   * Handles the dropping of a drag-and-drop event.
   *
   * @param {CdkDragDrop<Bookmark[]>} event - The event triggered when an item is dropped, including the previous and current container details.
   * @return {void} This method does not return any value.
   */
  drop(event: CdkDragDrop<Bookmark[]>): void {
    this.itemDropped.emit(event);
  }

  /**
   * Handles the event when the context menu is opened.
   *
   * @param {any} event - The event object associated with the context menu opening.
   * @param {Bookmark} data - The bookmark data to be used for updating the context menu.
   * @return {void} No return value.
   */
  onContextMenuOpen(event: any, data: Bookmark): void {
    this.updateContextMenuBookmarks(data);
  }

  /**
   * Updates the context menu with the appropriate options for bookmarks based on the given bookmark's data
   * and the user's permissions for the associated category.
   *
   * @param {Bookmark} data - The bookmark object containing information such as category and state (e.g., published or not).
   * @return {void} Does not return a value.
   */
  updateContextMenuBookmarks(data: Bookmark): void {
    const items: MenuItem[] = [
      {
        visible: this.permissionService.checkBookmarkRights(
          data.category || '',
          'edit'
        ),
        label: 'bearbeiten',
        icon: 'fas fa-pen',
        command: event => this.editBookmark(data),
      },
      {
        visible: this.permissionService.checkBookmarkRights(
          data.category || '',
          'read'
        ),
        label: 'öffnen',
        icon: 'fas fa-arrow-up-right-from-square',
        command: event => this.openBookmark(data),
      },
      {
        visible: this.permissionService.checkBookmarkRights(
          data.category || '',
          'read'
        ),
        label: 'teilen',
        disabled: data.private,
        icon: 'fas fa-share',
        items: [
          {
            label: 'intern',
            icon: 'fas fa-house-user',
            command: event => this.copyBookmarkLink(data, false),
          },
          {
            disabled: !data.published,
            label: 'öffentlich',
            icon: 'fas fa-earth-europe',
            command: event => this.copyBookmarkLink(data, true),
          },
        ],
      },
      {
        visible: this.permissionService.checkBookmarkRights(
          data.category || '',
          'edit'
        ),
        label: 'Startansicht',
        icon: 'fas fa-star',
        items: [
          {
            label: 'intern',
            icon: 'fas fa-house-user',
            command: event => this.makeDefault(data, false),
          },
          {
            label: 'öffentlich',
            icon: 'fas fa-earth-europe',
            command: event => this.makeDefault(data, true),
          },
        ],
      },
      {
        visible: data.guestLink?.enabled,
        label: 'Gast-Link',
        icon: 'fas fa-link',
        items: [
          {
            label: 'kopieren',
            icon: 'fas fa-copy',
            command: event =>
              this.guestLinkService.copyGuestLink(data.guestLink?.token || ''),
          },
          {
            label: 'öffnen',
            icon: 'fas fa-arrow-up-right-from-square',
            command: event =>
              this.guestLinkService.openGuestLink(data.guestLink?.token || ''),
          },
        ],
      },
      {
        visible:
          this.permissionService.checkBookmarkRights(
            data.category || '',
            'unlock'
          ) && !data.published,
        label: 'veröffentlichen',
        icon: 'fas fa-earth-europe',
        command: event => this.publishBookmark(data),
      },
      {
        visible:
          this.permissionService.checkBookmarkRights(
            data.category || '',
            'lock'
          ) && data.published,
        label: 'intern schalten',
        icon: 'fas fa-house-user',
        command: event => this.lockBookmark(data),
      },
      {
        visible: this.permissionService.checkBookmarkRights(
          data.category || '',
          'remove'
        ),
        label: 'löschen',
        icon: 'fas fa-trash',
        command: event => this.deleteBookmark(event, data),
      },
    ];

    this.contextMenuBookmarks = checkVisibleMenuItems(items);
  }

  /**
   * Navigates to the edit view of the specified bookmark.
   *
   * @param {Object} bookmark - The bookmark object to be edited.
   * @param {string} bookmark._id - The unique identifier of the bookmark.
   * @return {void} This method does not return a value.
   */
  editBookmark(bookmark: any): void {
    this.router.navigate(
      [
        {
          outlets: {
            bearbeiten: [bookmark._id],
          },
        },
      ],
      { relativeTo: this.route }
    );
  }

  /**
   * Deletes a specified bookmark after confirmation.
   *
   * @param {MenuItemCommandEvent} event - The event triggered by the user interaction.
   * @param {Bookmark} bookmark - The bookmark object that is to be deleted.
   * @return {void} Does not return a value.
   */
  deleteBookmark(event: MenuItemCommandEvent, bookmark: Bookmark): void {
    this.confirm(event.originalEvent, bookmark, bookmark.title);
  }

  /**
   * Publishes a bookmark by updating its status to published and notifying the user.
   *
   * @param {Bookmark} bookmark - The bookmark object to be published.
   * @return {void} No return value.
   */
  publishBookmark(bookmark: Bookmark): void {
    bookmark.published = true;
    this.store.update(bookmark);

    this.messageService.add({
      severity: 'info',
      summary: 'Ansicht erfolgreich veröffentlicht',
      life: 3000,
    });
  }

  lockBookmark(bookmark: Bookmark) {
    bookmark.published = false;
    this.store.update(bookmark);

    this.messageService.add({
      severity: 'info',
      summary: 'Ansicht erfolgreich gesperrt',
      life: 3000,
    });
  }

  /**
   * Tracks items by returning a unique identifier for a given item.
   *
   * @param {number} index - The index of the current item in the collection.
   * @param {Bookmark} item - The item from the collection to be tracked.
   * @return {string} The unique identifier for the given item, based on its title.
   */
  trackByItem(index: number, item: Bookmark): string {
    return item.title;
  }

  getBookmarkLink(data: Bookmark, published: boolean): string {
    return published
      ? `${window.location.origin}/atlas/${this.activeUserStore.commune()}/ansicht/${data._id}`
      : `${window.location.origin}/atlas-intern/ansicht/${data._id}`;
  }

  /**
   * Copies a bookmark link to the clipboard, with a notification indicating success or failure.
   *
   * @param {Bookmark} data - The bookmark data used to generate the link.
   * @param {boolean} published - Indicates whether the bookmark is published or not.
   * @return {void} This method does not return a value.
   */
  copyBookmarkLink(data: Bookmark, published: boolean): void {
    const link = this.getBookmarkLink(data, published);
    navigator.clipboard
      .writeText(link)
      .then(() => {
        this.messageService.add({
          severity: 'info',
          summary: 'Ansicht-Link in die Zwischenablage kopiert',
          life: 3000,
        });
      })
      .catch(() => {
        this.messageService.add({
          severity: 'error',
          summary: 'Fehler beim Kopieren des Links',
          life: 3000,
        });
      });
  }

  /**
   * Sets the specified bookmark as the default.
   *
   * @param {Bookmark} bookmark - The bookmark object to be set as the default.
   * @param {boolean} published - Indicates whether the bookmark is published.
   * @return {void} This method does not return a value.
   */
  makeDefault(bookmark: Bookmark, published: boolean): void {
    const activeCommune = this.globalsStore.activeCommune();
    if (!activeCommune) return;
    if (published) {
      activeCommune.defaultBookmarkExternal = bookmark._id;
    } else {
      activeCommune.defaultBookmark = bookmark._id;
    }
    activeCommune.message = {
      severity: 'success',
      summary: 'Ansicht erfolgreich als Startansicht festgelegt',
      life: 3000,
    };
    this.communeStore.update(activeCommune);
  }

  /**
   * Opens a bookmark link in a new browser tab.
   *
   * @param {Bookmark} data - The bookmark data used to construct the link.
   * @param {boolean} [published=false] - Specifies whether to use the published version of the bookmark.
   * @return {void} This method does not return any value.
   */
  private openBookmark(data: Bookmark, published = false) {
    const link = this.getBookmarkLink(data, published);
    //open new browsertab with link
    window.open(link, '_blank');
  }
}
